import { BusinessDownloadConfig, FormOptions } from '../types';
import {
  ECHARTS_DATASOURCE_METRICS_COMBINE_DETAILS,
  ECHARTS_DATASOURCE_METRICS_COMBINE_KPITRENDS,
  ECHARTS_DATASOURCE_METRICS_COMBINE_KPITRENDS_NEW,
  ECHARTS_DATASOURCE_METRICS_COMBINE_DETAILS_COMPARE,
} from '@intelligence/utils/constant';
import { DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API, publisherCheckboxList } from './common';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import InfoDoc from '@intelligence/_locales/en-US/infoDoc';
import InfoDocCN from '@intelligence/_locales/zh-CN/infoDoc';
import { cloneDeep } from 'lodash-es';

const getDeatilDowloadMetricCascadeOption = (cascadeOptions: any) => {
  const options = cloneDeep(cascadeOptions);
  // alinea 的 DAU / MAU 仍归属于 Alinea 分组，不再搬运到 Steam（source_steam）分组下
  // console.debug('getDeatilDowloadMetricCascadeOption', {
  //   cascadeOptions,
  //   options,
  //   ECHARTS_DATASOURCE_METRICS_COMBINE_DETAILS_COMPARE,
  // });

  return options?.map((item: any) => {
    const icon = getDataSourceIcon(item.icon ? item.icon : item.value);
    if (item.children) {
      item.children = item?.children.map((child: any) => {
        const childIcon = getDataSourceIcon(child.icon || child.tableSource);
        return {
          ...child,
          icon: childIcon || icon,
          value: `${item.value}###${child.value}`,
        };
      });
    }
    return {
      ...item,
      icon,
    };
  });
};

const getKPITrendsDowloadMetricCascadeOption = (cascadeOptions: any) => {
  const options = cloneDeep(cascadeOptions);
  const alineaOption = options?.find((item: any) => item?.value === 'alinea');
  const alineaChildren = alineaOption?.children || [];

  if (alineaOption && !alineaChildren.some((item: any) => item?.value === 'alinea__dau')) {
    const insertAfterIndex = alineaChildren.findIndex(
      (item: any) => item?.value === 'alinea__wishlists_total',
    );
    const insertAt =
      insertAfterIndex >= 0 ? insertAfterIndex + 1 : alineaChildren.length;
    const activeMetrics = [
      {
        text: 'dbi_metric_alinea_dau',
        value: 'alinea__dau',
        icon: 'alinea',
        tableSource: alineaOption.tableSource,
        ableDateType: 'day',
        message: InfoDoc.dbi_info_alinea_dau_metric_disabled_tips,
        messageCN: InfoDocCN.dbi_info_alinea_dau_metric_disabled_tips,
      },
      {
        text: 'dbi_metric_alinea_mau',
        value: 'alinea__mau',
        icon: 'alinea',
        tableSource: alineaOption.tableSource,
        ableDateType: 'month',
        message: InfoDoc.dbi_info_alinea_mau_metric_disabled_tips,
        messageCN: InfoDocCN.dbi_info_alinea_mau_metric_disabled_tips,
      },
    ];

    alineaOption.children = [
      ...alineaChildren.slice(0, insertAt),
      ...activeMetrics,
      ...alineaChildren.slice(insertAt),
    ];
  }

  return getDeatilDowloadMetricCascadeOption(options);
};

const BasicInformation = {
  ...publisherCheckboxList,
  options: publisherCheckboxList?.options?.filter((item: FormOptions) => {
    return item.value !== 'region_show';
  }),
};

const BasicInformationWithSteamTag = {
  ...publisherCheckboxList,
  options: publisherCheckboxList?.options?.filter((item: FormOptions) => {
    return item.value !== 'region_show';
  }),
};

BasicInformationWithSteamTag.options = BasicInformation?.options?.concat([
  { label: 'dbi_top_steam_tags', value: 'steam_tag_show' },
  { label: 'dbi_top_game_mode', value: 'game_mode_show', isNeedMoreMarginTop: true },
]);

const combineDetailsConfig: BusinessDownloadConfig = {
  'combine-stats-countries': {
    apiUrl: DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API,
    bubbleText: '',
    translate: true,
    formList: [
      {
        className: 'dbi-top-companies-source-metric-selector',
        label: 'dbi_label_metrics',
        type: 'sourceMetricSelectorDetail',
        field: 'metrics',
        defaultValue: [],
        cascadeOptions: getDeatilDowloadMetricCascadeOption(
          // ECHARTS_DATASOURCE_METRICS_COMBINE_DETAILS,
          // 统一使用 KPITrends 的详情页 metrics option 配置
          ECHARTS_DATASOURCE_METRICS_COMBINE_KPITRENDS,
        ),
      },
      BasicInformationWithSteamTag,
      {
        label: 'dbi_label_data_calculation',
        type: 'subForm',
        children: [
          {
            label: 'dbi_label_time_settings',
            type: 'radio',
            defaultValue: 'separate',
            field: 'downlaod_date_method',
            // message:
            //   'The metric of Active Users has different granularities, you can get DAU/WAU/MAU when selecting the below options.',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_time_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_region_settings',
            type: 'radio',
            field: 'region_method',
            defaultValue: 'aggregated',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_country_combine', value: 'market' },
              { label: 'dbi_label_separate_by_region_combine', value: 'region' },
            ],
          },
          {
            label: 'dbi_label_device_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'device_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_device_combine', value: 'separate' },
              { label: 'dbi_separate_by_device', value: 'device' },
            ],
            message: 'dbi_pc_console_download_by_device_tip',
          },
          {
            label: 'dbi_label_segment_settings_product',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'segment_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_segment_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_ratio',
            type: 'switch',
            defaultValue: true,
            field: 'ratio_show',
            options: [],
          },
        ],
      },
    ],
  },
  'combine-detail-kpitends': {
    apiUrl: DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API,
    bubbleText: '',
    translate: true,
    formList: [
      {
        className: 'dbi-top-companies-source-metric-selector',
        label: 'dbi_label_metrics',
        type: 'sourceMetricSelectorDetail',
        field: 'metrics',
        defaultValue: [],
        cascadeOptions: getDeatilDowloadMetricCascadeOption(
          ECHARTS_DATASOURCE_METRICS_COMBINE_KPITRENDS_NEW,
        ),
      },
      BasicInformationWithSteamTag,
      {
        label: 'dbi_label_data_calculation',
        type: 'subForm',
        children: [
          {
            label: 'dbi_label_time_settings',
            type: 'radio',
            defaultValue: 'separate',
            field: 'downlaod_date_method',
            // message:
            //   'The metric of Active Users has different granularities, you can get DAU/WAU/MAU when selecting the below options.',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_time_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_region_settings',
            type: 'radio',
            field: 'region_method',
            defaultValue: 'aggregated',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_country_combine', value: 'market' },
              { label: 'dbi_label_separate_by_region_combine', value: 'region' },
            ],
          },
          {
            label: 'dbi_label_device_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'device_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_device_combine', value: 'separate' },
              { label: 'dbi_separate_by_device', value: 'device' },
            ],
            message: 'dbi_pc_console_download_by_device_tip',
          },
          {
            label: 'dbi_label_segment_settings_product',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'segment_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_segment_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_ratio',
            type: 'switch',
            defaultValue: true,
            field: 'ratio_show',
            options: [],
          },
        ],
      },
    ],
  },
  'combine-detail-kpitends-hourly': {
    apiUrl: DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API,
    bubbleText: '',
    translate: true,
    formList: [],
  },
  'combine-detail-allmetrics': {
    apiUrl: DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API,
    bubbleText: '',
    translate: true,
    formList: [
      {
        className: 'dbi-top-companies-source-metric-selector',
        label: 'dbi_label_metrics',
        type: 'sourceMetricSelectorDetail',
        field: 'metrics',
        defaultValue: [],
        cascadeOptions: getDeatilDowloadMetricCascadeOption(
          ECHARTS_DATASOURCE_METRICS_COMBINE_KPITRENDS_NEW,
        ),
      },
      BasicInformationWithSteamTag,
      {
        label: 'dbi_label_data_calculation',
        type: 'subForm',
        children: [
          {
            label: 'dbi_label_time_settings',
            type: 'radio',
            defaultValue: 'separate',
            field: 'downlaod_date_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_time_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_region_settings',
            type: 'radio',
            field: 'region_method',
            defaultValue: 'aggregated',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_country_combine', value: 'market' },
              { label: 'dbi_label_separate_by_region_combine', value: 'region' },
            ],
          },
          {
            label: 'dbi_label_device_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'device_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_device_combine', value: 'separate' },
              { label: 'dbi_separate_by_device', value: 'device' },
            ],
            message: 'dbi_pc_console_download_by_device_tip',
          },
          {
            label: 'dbi_label_segment_settings_product',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'segment_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_segment_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_ratio',
            type: 'switch',
            defaultValue: true,
            field: 'ratio_show',
            options: [],
          },
        ],
      },
    ],
  },
  'combine-detail-steamdata': {
    apiUrl: DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API,
    bubbleText: '',
    translate: true,
    formList: [
      {
        className: 'dbi-top-companies-source-metric-selector',
        label: 'dbi_label_metrics',
        type: 'sourceMetricSelectorDetail',
        field: 'metrics',
        defaultValue: [],
        cascadeOptions: getDeatilDowloadMetricCascadeOption(
          ECHARTS_DATASOURCE_METRICS_COMBINE_KPITRENDS_NEW,
        ),
      },
      BasicInformationWithSteamTag,
      {
        label: 'dbi_label_data_calculation',
        type: 'subForm',
        children: [
          {
            label: 'dbi_label_time_settings',
            type: 'radio',
            defaultValue: 'separate',
            field: 'downlaod_date_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_time_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_region_settings',
            type: 'radio',
            field: 'region_method',
            defaultValue: 'aggregated',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_country_combine', value: 'market' },
              { label: 'dbi_label_separate_by_region_combine', value: 'region' },
            ],
          },
          {
            label: 'dbi_label_device_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'device_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_device_combine', value: 'separate' },
              { label: 'dbi_separate_by_device', value: 'device' },
            ],
            message: 'dbi_pc_console_download_by_device_tip',
          },
          {
            label: 'dbi_label_segment_settings_product',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'segment_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_segment_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_ratio',
            type: 'switch',
            defaultValue: true,
            field: 'ratio_show',
            options: [],
          },
        ],
      },
    ],
  },
  'combine-detail-consoledata': {
    apiUrl: DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API,
    bubbleText: '',
    translate: true,
    formList: [
      {
        className: 'dbi-top-companies-source-metric-selector',
        label: 'dbi_label_metrics',
        type: 'sourceMetricSelectorDetail',
        field: 'metrics',
        defaultValue: [],
        cascadeOptions: getDeatilDowloadMetricCascadeOption(
          ECHARTS_DATASOURCE_METRICS_COMBINE_KPITRENDS_NEW,
        ),
      },
      BasicInformationWithSteamTag,
      {
        label: 'dbi_label_data_calculation',
        type: 'subForm',
        children: [
          {
            label: 'dbi_label_time_settings',
            type: 'radio',
            defaultValue: 'separate',
            field: 'downlaod_date_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_time_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_region_settings',
            type: 'radio',
            field: 'region_method',
            defaultValue: 'aggregated',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_country_combine', value: 'market' },
              { label: 'dbi_label_separate_by_region_combine', value: 'region' },
            ],
          },
          {
            label: 'dbi_label_device_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'device_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_device_combine', value: 'separate' },
              { label: 'dbi_separate_by_device', value: 'device' },
            ],
            message: 'dbi_pc_console_download_by_device_tip',
          },
          {
            label: 'dbi_label_segment_settings_product',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'segment_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_segment_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_ratio',
            type: 'switch',
            defaultValue: true,
            field: 'ratio_show',
            options: [],
          },
        ],
      },
    ],
  },
  'combine-stats-countries-compare': {
    apiUrl: DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API,
    bubbleText: '',
    translate: true,
    formList: [
      {
        className: 'dbi-top-companies-source-metric-selector',
        label: 'dbi_label_metrics',
        type: 'sourceMetricSelectorDetail',
        field: 'metrics',
        defaultValue: [],
        // defaultValue: DATASOURCE_METRICS_VALUE_PC_DETAILS,
        cascadeOptions: getDeatilDowloadMetricCascadeOption(
          ECHARTS_DATASOURCE_METRICS_COMBINE_DETAILS_COMPARE,
        ),
      },
      BasicInformation,
      {
        label: 'dbi_label_data_calculation',
        type: 'subForm',
        children: [
          {
            label: 'dbi_label_time_settings',
            type: 'radio',
            defaultValue: 'separate',
            field: 'downlaod_date_method',
            // message:
            //   'The metric of Active Users has different granularities, you can get DAU/WAU/MAU when selecting the below options.',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_time_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_region_settings',
            type: 'radio',
            field: 'region_method',
            defaultValue: 'aggregated',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_country_combine', value: 'market' },
              { label: 'dbi_label_separate_by_region_combine', value: 'region' },
            ],
          },
          {
            label: 'dbi_label_device_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'device_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_device_combine', value: 'separate' },
              { label: 'dbi_separate_by_device', value: 'device' },
            ],
            message: 'dbi_pc_console_download_by_device_tip',
          },
          {
            label: 'dbi_label_segment_settings_product',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'segment_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_segment_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_ratio',
            type: 'switch',
            defaultValue: true,
            field: 'ratio_show',
            options: [],
          },
        ],
      },
    ],
  },
  'combine-stats-countries-pcu': {
    apiUrl: DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API,
    bubbleText: '',
    translate: true,
    formList: [
      {
        className: 'dbi-top-companies-source-metric-selector',
        label: 'dbi_label_metrics',
        type: 'sourceMetricSelectorDetail',
        field: 'metrics',
        defaultValue: [],
        // defaultValue: DATASOURCE_METRICS_VALUE_PC_DETAILS,
        cascadeOptions: getDeatilDowloadMetricCascadeOption(
          ECHARTS_DATASOURCE_METRICS_COMBINE_DETAILS,
        ),
      },

      BasicInformationWithSteamTag,

      {
        label: 'dbi_label_data_calculation',
        type: 'subForm',
        children: [
          {
            label: 'dbi_label_time_settings',
            type: 'radio',
            defaultValue: 'separate',
            field: 'downlaod_date_method',
            // message:
            //   'The metric of Active Users has different granularities, you can get DAU/WAU/MAU when selecting the below options.',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_time_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_region_settings',
            type: 'radio',
            field: 'region_method',
            defaultValue: 'aggregated',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_country_combine', value: 'market' },
              { label: 'dbi_label_separate_by_region_combine', value: 'region' },
            ],
          },
          {
            label: 'dbi_label_device_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'device_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_device_combine', value: 'separate' },
              { label: 'dbi_separate_by_device', value: 'device' },
            ],
            message: 'dbi_pc_console_download_by_device_tip',
          },
          {
            label: 'dbi_label_segment_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'segment_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_segment_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_ratio',
            type: 'switch',
            defaultValue: true,
            field: 'ratio_show',
            options: [],
          },
        ],
      },
    ],
  },
  'combine-stats-countries-japan-sales': {
    apiUrl: DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API,
    bubbleText: '',
    translate: true,
    formList: [
      {
        className: 'dbi-top-companies-source-metric-selector',
        label: 'dbi_label_metrics',
        type: 'sourceMetricSelectorDetail',
        field: 'metrics',
        defaultValue: [],
        // defaultValue: DATASOURCE_METRICS_VALUE_PC_DETAILS,
        cascadeOptions: getDeatilDowloadMetricCascadeOption(
          ECHARTS_DATASOURCE_METRICS_COMBINE_DETAILS,
        ),
      },

      BasicInformationWithSteamTag,

      {
        label: 'dbi_label_data_calculation',
        type: 'subForm',
        children: [
          {
            label: 'dbi_label_time_settings',
            type: 'radio',
            defaultValue: 'separate',
            field: 'downlaod_date_method',
            // message:
            //   'The metric of Active Users has different granularities, you can get DAU/WAU/MAU when selecting the below options.',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_time_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_region_settings',
            type: 'radio',
            field: 'region_method',
            defaultValue: 'aggregated',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_country_combine', value: 'market' },
              { label: 'dbi_label_separate_by_region_combine', value: 'region' },
            ],
          },
          {
            label: 'dbi_label_device_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'device_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_device_combine', value: 'separate' },
              { label: 'dbi_separate_by_device', value: 'device' },
            ],
            message: 'dbi_pc_console_download_by_device_tip',
          },
          {
            label: 'dbi_label_segment_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'segment_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_segment_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_ratio',
            type: 'switch',
            defaultValue: true,
            field: 'ratio_show',
            options: [],
          },
        ],
      },
    ],
  },
  'combine-stats-countries-ampere': {
    apiUrl: DETAILS_STATS_COUNTRIES_COMMON_DOWNLOAD_API,
    bubbleText: '',
    translate: true,
    formList: [
      {
        className: 'dbi-top-companies-source-metric-selector',
        label: 'dbi_label_metrics',
        type: 'sourceMetricSelectorDetail',
        field: 'metrics',
        defaultValue: [],
        cascadeOptions: getDeatilDowloadMetricCascadeOption(
          // ECHARTS_DATASOURCE_METRICS_COMBINE_DETAILS,
          // 统一使用 KPITrends 的详情页 metrics option 配置
          ECHARTS_DATASOURCE_METRICS_COMBINE_KPITRENDS,
        ),
      },
      BasicInformationWithSteamTag,
      {
        label: 'dbi_label_data_calculation',
        type: 'subForm',
        children: [
          {
            label: 'dbi_label_time_settings',
            type: 'radio',
            defaultValue: 'separate',
            field: 'downlaod_date_method',
            // message:
            //   'The metric of Active Users has different granularities, you can get DAU/WAU/MAU when selecting the below options.',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_time_combine', value: 'separate' },
            ],
          },
          {
            label: 'dbi_label_region_settings',
            type: 'radio',
            field: 'region_method',
            defaultValue: 'aggregated',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_country_combine', value: 'market' },
              { label: 'dbi_label_separate_by_region_combine', value: 'region' },
            ],
          },
          {
            label: 'dbi_label_device_settings',
            type: 'radio',
            defaultValue: 'aggregated',
            field: 'device_method',
            options: [
              { label: 'dbi_label_aggregated', value: 'aggregated' },
              { label: 'dbi_label_separate_by_device_combine', value: 'separate' },
              { label: 'dbi_separate_by_device', value: 'device' },
            ],
            message: 'dbi_pc_console_download_by_device_tip',
          },
          // ampere 配置不展示 by segment
          // {
          //   label: 'dbi_label_segment_settings',
          //   type: 'radio',
          //   defaultValue: 'aggregated',
          //   field: 'segment_method',
          //   options: [
          //     { label: 'dbi_label_aggregated', value: 'aggregated' },
          //     { label: 'dbi_label_separate_by_segment_combine', value: 'separate' },
          //   ],
          // },
          {
            label: 'dbi_label_ratio',
            type: 'switch',
            defaultValue: true,
            field: 'ratio_show',
            options: [],
          },
        ],
      },
    ],
  },
};

export default combineDetailsConfig;
